import { useT } from "@/i18n";
import { useSiteContent, useSiteText } from "@/context/SiteContentContext";
import Seo from "@/components/Seo";

// Contact page — all rows are editable from admin. Falls back to sensible
// defaults for a fresh install.
export default function Contact() {
    const { t, lang } = useT();
    const { content } = useSiteContent();
    const st = useSiteText(lang, t);

    // Assemble up to 6 rows: any row with both label + value is shown; empty rows are skipped.
    const rows = [1, 2, 3, 4, 5, 6]
        .map((n) => ({
            label: content[`contact_row_${n}_label_${lang}`] || content[`contact_row_${n}_label_en`] || "",
            value: content[`contact_row_${n}_value`] || "",
            href: content[`contact_row_${n}_href`] || "",
        }))
        .filter((r) => r.label && r.value);

    // Fallback defaults if nothing configured yet
    const fallbackRows = [
        { label: lang === "es" ? "bookings + general" : "bookings + general", value: "hello@itzkatu.studio", href: "mailto:hello@itzkatu.studio" },
        { label: lang === "es" ? "prensa" : "press",                          value: "press@itzkatu.studio", href: "mailto:press@itzkatu.studio" },
        { label: "instagram",                                                  value: "@itzkatu",             href: "https://instagram.com/itzkatu" },
        { label: "soundcloud",                                                 value: "/itzkatu",             href: "https://soundcloud.com/itzkatu" },
    ];
    const finalRows = rows.length > 0 ? rows : fallbackRows;

    return (
        <div data-testid="contact-page" className="px-4 md:px-8 pt-14 md:pt-20 pb-6 md:pb-8 min-h-[70vh]">
            <Seo
                title="Contact | ITZKATU"
                description="contacto del colectivo itzkatu — bookings, colaboraciones y prensa."
                path="/contact"
            />
            <div className="grid grid-cols-12 gap-6 mb-14">
                <div className="col-span-12 md:col-span-8">
                    <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5D3FD3]">{t("nav_contact")}</div>
                    <h1 className="mt-4 font-heading font-black text-5xl sm:text-6xl md:text-8xl uppercase tracking-tighter leading-[0.9]">
                        {st("contact_h")}
                    </h1>
                    <p className="mt-6 max-w-xl font-body text-base md:text-lg text-[#333]">
                        {st("contact_intro")}
                    </p>
                </div>
            </div>

            <ul className="max-w-4xl">
                {finalRows.map((r, i) => (
                    <li key={`${r.label}-${i}`}>
                        <a
                            href={r.href || "#"}
                            target={r.href.startsWith("http") ? "_blank" : undefined}
                            rel="noreferrer"
                            data-testid={`contact-row-${i + 1}`}
                            className="grid grid-cols-12 items-center gap-4 py-5 md:py-6 group hover:bg-[#333] hover:text-[#FFFFFF] transition-colors duration-300"
                        >
                            <div className="col-span-4 md:col-span-3 font-mono text-xs uppercase tracking-[0.2em] text-[#555] group-hover:text-[#FFFFFF]">
                                {r.label}
                            </div>
                            <div className="col-span-8 md:col-span-9 font-heading font-bold text-xl md:text-3xl uppercase tracking-tight">
                                {r.value}
                            </div>
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}
